import { useState } from "react";
import { toast } from "sonner";
import { Wallet, Upload, CheckCircle2, FileText, FileDown } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { fileViewUrl, pdfUrl } from "@/lib/files";
import { money, formatDateTime } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const STATUS_STYLE = {
  pending: "bg-amber-50 text-amber-700 border-amber-200",
  sent: "bg-blue-50 text-blue-700 border-blue-200",
  received: "bg-emerald-50 text-emerald-700 border-emerald-200",
};

export function PartnerPayoutPanel({ order, onUpdated }) {
  const { user } = useAuth();
  const { t } = useI18n();
  const isStaff = ["admin", "support"].includes(user?.role);
  const payout = order.partner_payout || {};
  const [form, setForm] = useState({
    mode: payout.mode || "manual",
    percentage: payout.percentage ?? "",
    amount: payout.amount ?? "",
    status: payout.status || "pending",
    payment_date: payout.payment_date || "",
    reference: payout.reference || "",
    proof_document_id: payout.proof_document_id || null,
  });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      // Amount is owned by the assignment panel; here staff only track the actual payment.
      const { data } = await api.patch(`/orders/${order.id}/partner-payout`, {
        status: form.status, payment_date: form.payment_date || null,
        reference: form.reference || null, proof_document_id: form.proof_document_id || null,
      });
      onUpdated?.(data); toast.success(t("po.saved"));
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const uploadProof = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const fd = new FormData();
    fd.append("file", file); fd.append("entity_type", "order");
    fd.append("entity_id", order.id); fd.append("category", "payment_proof");
    try {
      const { data } = await api.post("/documents/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((s) => ({ ...s, proof_document_id: data.id }));
      toast.success(t("po.proof"));
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const confirmReceipt = async () => {
    try {
      const { data } = await api.post(`/orders/${order.id}/partner-payout/confirm`);
      onUpdated?.(data); toast.success(t("po.confirmed"));
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const statusLabel = (s) => ({ pending: t("po.pending"), sent: t("po.sent"), received: t("po.received") }[s] || s);

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6 space-y-4 h-fit" data-testid="partner-payout-panel">
      <h3 className="font-display text-lg font-semibold flex items-center gap-2"><Wallet size={18} /> {t("po.title")}</h3>

      {isStaff ? (
        <div className="space-y-3">
          <p className="text-xs text-slate-500 -mt-1">Registra qui il pagamento all'azienda <b>principale</b>. L'importo si imposta nel pannello «Aziende partner».</p>
          <div className="flex justify-between text-sm rounded-lg bg-slate-50 border border-slate-200 px-3 py-2">
            <span className="text-slate-500">{t("po.amount")}</span>
            <span className="font-semibold text-slate-800" data-testid="payout-amount-readonly">{payout.amount != null ? money(payout.amount, order.currency) : "—"}</span>
          </div>
          {payout.amount == null && (
            <p className="text-xs text-amber-600" data-testid="payout-no-primary">Assegna prima un'azienda principale nel pannello «Aziende partner».</p>
          )}
          <div className="space-y-1.5"><Label>{t("po.status")}</Label>
            <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
              <SelectTrigger data-testid="payout-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="pending">{t("po.pending")}</SelectItem>
                <SelectItem value="sent">{t("po.sent")}</SelectItem>
                <SelectItem value="received">{t("po.received")}</SelectItem>
              </SelectContent>
            </Select></div>
          <div className="space-y-1.5"><Label>{t("po.date")}</Label>
            <Input type="date" value={form.payment_date} onChange={(e) => setForm({ ...form, payment_date: e.target.value })} data-testid="payout-date" /></div>
          <div className="space-y-1.5"><Label>{t("po.reference")}</Label>
            <Input value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })} data-testid="payout-reference" /></div>
          <div className="space-y-1.5"><Label>{t("po.proof")}</Label>
            <div className="flex items-center gap-2">
              <input type="file" id="payout-proof-input" className="hidden" onChange={uploadProof} data-testid="payout-proof-input" />
              <Button variant="outline" size="sm" onClick={() => document.getElementById("payout-proof-input")?.click()} data-testid="payout-proof-btn"><Upload size={14} className="mr-1.5" /> {t("po.uploadProof")}</Button>
              {form.proof_document_id && <a href={fileViewUrl(form.proof_document_id)} target="_blank" rel="noreferrer" className="text-xs text-blue-700 hover:underline flex items-center gap-1"><FileText size={13} /> {t("po.proof")}</a>}
            </div></div>
          <Button onClick={save} disabled={saving || payout.amount == null} className="w-full bg-blue-900 hover:bg-blue-800" data-testid="payout-save-btn">{saving ? "..." : t("po.save")}</Button>
        </div>
      ) : payout.amount ? (
        <div className="space-y-3 text-sm" data-testid="payout-partner-view">
          <div className="flex items-center justify-between">
            <span className="text-slate-500">{t("po.amount")}</span>
            <span className="font-semibold text-slate-900">{money(payout.amount, order.currency)}</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-slate-500">{t("po.status")}</span>
            <span className={`text-xs px-2 py-0.5 rounded-full border ${STATUS_STYLE[payout.status] || ""}`} data-testid="payout-status-badge">{statusLabel(payout.status)}</span>
          </div>
          {payout.reference && <div className="flex items-center justify-between"><span className="text-slate-500">{t("po.reference")}</span><span className="text-slate-700">{payout.reference}</span></div>}
          {payout.sent_at && <div className="text-xs text-slate-400">{t("po.sent")}: {formatDateTime(payout.sent_at)}</div>}
          {payout.received_at && <div className="text-xs text-emerald-600">{t("po.received")}: {formatDateTime(payout.received_at)}</div>}
          {payout.proof_document_id && <a href={fileViewUrl(payout.proof_document_id)} target="_blank" rel="noreferrer" className="text-xs text-blue-700 hover:underline flex items-center gap-1"><FileText size={13} /> {t("po.proof")}</a>}
          {payout.status !== "received" && (
            <Button onClick={confirmReceipt} className="w-full bg-emerald-600 hover:bg-emerald-700" data-testid="payout-confirm-btn"><CheckCircle2 size={16} className="mr-1.5" /> {t("po.confirmReceipt")}</Button>
          )}
        </div>
      ) : (
        <p className="text-sm text-slate-400" data-testid="payout-none">{t("po.none")}</p>
      )}

      {order.partner_invoice_id && (
        <a href={pdfUrl(`invoices/${order.partner_invoice_id}/pdf`)} target="_blank" rel="noreferrer"
           className="block" data-testid="partner-invoice-download">
          <Button variant="outline" size="sm" className="w-full mt-1">
            <FileDown size={14} className="mr-1.5" /> Scarica fattura partner
          </Button>
        </a>
      )}
    </div>
  );
}
